<script setup>
    import Modal from "../../../../../elements/Modal.vue";
    import Btn from "../../../../../elements/Btn.vue";
    import Icon from "../../../../../elements/Icon.vue";
    import RedemptionSpecificDatePeriod from "../settings/RedemptionSpecificDatePeriod.vue";
    import Spinner from "../../../../../elements/Spinner.vue";
    import TextField from "../../../../../elements/forms/TextField.vue";
    import RedemptionTimesPeriod from "../settings/RedemptionTimesPeriod.vue";
    import Tooltip from "../../../../../elements/Tooltip.vue";
    import {reactive, ref} from "vue";
    import Notification from "../../../../../elements/Notification.vue";

    const props = defineProps({
        program: {
            type: Object,
            required: true,
        },
        couponId: {
            type: Number,
            required: true,
        },
        promotionCode: {
            type: Object,
            required: true,
        },
        promotionCodeAction: {
            type: String,
            required: true,
        }
    });

    const promoCode = reactive(props.promotionCode);

    const promoCodeAlreadySaved = () => {return props.promotionCodeAction === 'edit' && !!props.promotionCode.id }

    const editingAction = props.promotionCodeAction === 'edit'

    const updatingAutoAssignablePromotionCode = ref(false);

    const savingAutoAssignablePromotionCode = ref(false);

    const autoGeneratePromotionCodeNameBuilderAvailableFields = [
        { value: 'name', label: 'Partner name', example: 'Jack'},
        { value: 'surname', label: 'Partner last name', example: 'Partnero'},
        { value: 'email', label: 'Partner email address', example: 'jack@partnero.com'},
        { value: 'discount_amount', label: 'Discount amount', example: '10'},
        { value: 'coupon_name', label: 'Coupon name', example: 'DISCOUNT2024'},
        { value: 'partner_identifier', label: 'Partner key', example: '29UN6Em3iVD6'},
        { value: 'random_characters', label: 'Random characters', example: 'AdXjwO4D'},
    ]

    const autoGeneratePromotionCodeNameFieldRandomSymbolsCountOptions = [
        { value: 3, label: '3 characters' },
        { value: 4, label: '4 characters' },
        { value: 5, label: '5 characters' },
        { value: 6, label: '6 characters' },
        { value: 7, label: '7 characters' },
    ];

    const autoGeneratePromotionCodeNameFieldExample = () => {
        let example = '';

        if (promoCode.auto_generate_promotion_code_name_fields.length === 0) {
            return promoCode.auto_generate_promotion_code_name_custom_prefix + '0brAnRUB';
        }

        promoCode.auto_generate_promotion_code_name_fields.forEach(field => {
            switch (field) {
                case 'name':
                    example += getFieldExample('name');
                    break;
                case 'surname':
                    example += getFieldExample('surname');
                    break;
                case 'email':
                    example += getFieldExample('email').split('@')[0];
                    break;
                case 'discount_amount':
                    example += getFieldExample('discount_amount')
                    break;
                case 'coupon_name':
                    example += getFieldExample('coupon_name')
                    break;
                case 'random_characters':
                    example += getFieldExample('random_characters').substring(0, promoCode.auto_generate_promotion_code_name_random_characters_count);
                    break;
                case 'partner_identifier':
                    example += getFieldExample('partner_identifier');
                    break;
            }
        });

        return promoCode.auto_generate_promotion_code_name_custom_prefix + example;
    }

    const getFieldExample = (fieldValue) => {
        return autoGeneratePromotionCodeNameBuilderAvailableFields.find(field => field.value === fieldValue).example;
    }

    const notification = reactive({
        showNotification: false,
        notificationTitle: 'Error',
        notificationType: 'warning',
        notificationMessage: 'Action error, see console for error details.'
    });

    const promotionCodeExpired = (date) => {
        return date && new Date(date) < new Date()
    }

    const removeMetadata = (metadataIndex) => {
        promoCode.metadata.splice(metadataIndex, 1);
    }

    const toggleActivePromoCode = () => {
        if (promoCode.id) {
            axios.patch(route('app_int.promotion-code.toggle-active', {program: props.program, coupon: props.couponId, promoCode: promoCode.id}))
                .then(response => {
                    const status = parseInt(response.data.status || '-1');
                    if (status === 1) {
                        window.location.reload();
                    }
                })
                .catch(error => {
                    notification.notificationType = 'warning'
                    notification.showNotification = true
                    console.log(error, error.response)
                })
        }
        else {
            // reload promo code page
        }
    }

    const addMetadata = () => {
        promoCode.metadata.push({ key: '', value: '' });
    }

    const updateAutoAssignablePromotionCodeMetadata = () => {
        updatingAutoAssignablePromotionCode.value = true;
        axios.patch(route('app_int.promotion-code.update', {program: props.program, coupon: props.couponId, promoCode: promoCode.id}), {
            metadata: promoCode.metadata,
            'limit_to_specific_partner': promoCode.limit_to_specific_partner,
            'coupon_specific_partners': promoCode.coupon_specific_partners,
            'limit_to_specific_customer': promoCode.limit_to_specific_customer,
            'coupon_specific_customers': promoCode.coupon_specific_customers,
        }).then(response => {
            const status = parseInt(response.data.status || '-1');
            if (status === 1) {
                window.location.reload();
            } else {
                notification.notificationType = 'warning';
                notification.showNotification = true;
            }
        }).catch(error => {
            console.log(error)
            if (error.response?.status === 422) {
                if (error.response?.data?.message) {
                    notification.notificationMessage = error.response.data.message;
                    if (error.response.data.errors) {
                        notification.notificationMessage += ' ' + Object.values(error.response.data.errors).join(' ');
                    }
                }
            }
            notification.notificationType = 'warning';
            notification.showNotification = true;
            console.log(error)
        }).finally(() => {
            updatingAutoAssignablePromotionCode.value = false;
        })
    }

    const saveAutoAssignablePromotionCode = () => {
        savingAutoAssignablePromotionCode.value = true;
        axios.post(route('app_int.auto-assignable-promotion-code.create', {program: props.program, coupon: props.couponId}), {
            coupon_id: props.couponId,
            auto_generate_promotion_code: promoCode.auto_generate_promotion_code,
            auto_generate_promotion_code_name_fields: promoCode.auto_generate_promotion_code_name_fields,
            auto_generate_promotion_code_name_custom_prefix: promoCode.auto_generate_promotion_code_name_custom_prefix,
            auto_generate_promotion_code_name_random_characters_count: promoCode.auto_generate_promotion_code_name_random_characters_count,
            first_time_order: promoCode.first_time_order,
            minimum_order_status: promoCode.minimum_order_status,
            minimum_order_value: promoCode.minimum_order_value,
            expiration_date_status: promoCode.expiration_date_status,
            expiration_date_value: promoCode.expiration_date_value,
            redemption_times_status: promoCode.redemption_times_status,
            redemption_times_value: promoCode.redemption_times_value,
            metadata: promoCode.metadata,
        }).then(response => {
            const status = parseInt(response.data.status || '-1');
            if (status === 1) {
                window.location.reload();
            } else {
                notification.notificationType = 'warning';
                notification.showNotification = true;
            }
        }).catch(error => {
            if (error.response.status === 422) {
                notification.notificationMessage = error.response.data.message;
            }
            notification.notificationType = 'warning';
            notification.showNotification = true;
            console.log(error)
        }).finally(() => {
            savingAutoAssignablePromotionCode.value = false;
        })
    }

</script>

<template>
    <div>
        <notification
            :show="notification.showNotification"
            :title="notification.notificationTitle"
            :type="notification.notificationType"
            class="mb-20"
            @notificationClosed="notification.showNotification = false"
            whitespace="whitespace-normal"
        >
            <template #message>
                {{ notification.notificationMessage }}
            </template>
        </notification>
        <modal :title="promoCodeAlreadySaved() ? 'Promotion code details' : 'New promotion code'" ref="promoCodeModal">
            <template #toggle>
                <btn v-if="promotionCodeAction === 'create'" type="primary">New auto-generated promotion code</btn>
                <btn v-else type="ghost" icon-name="more" size="small"></btn>
            </template>
            <template #body>

                <div class="space-y-4">

                    <div class="text-sm text-gray-500">
                        Promotion codes will be automatically assigned to current partners, and newly registered partners will also receive a promotion code upon completing their registration.
                    </div>

                    <div class="border rounded">

                        <div class="flex items-center justify-between py-4 px-6 hidden">

                            <div class="flex items-center">
                                <div>
                                    <icon name="coupon" size="24" class="hover:text-gray-500" />
                                </div>
                                <div class="text-sm font-semibold text-gray-900 ml-4">
                                    {{ autoGeneratePromotionCodeNameFieldExample() }}
                                    <span v-if="promotionCodeExpired(promoCode?.expiration_date_value)"
                                          class="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs border align-middle font-medium bg-pink-100 text-pink-800 border-pink-100"
                                    >
                                        Expired
                                    </span>
                                    <span v-if="!promoCode?.active"
                                          class="align-middle bg-gray-200 border border-gray-400 inline-flex items-center ml-1 px-1.5 py-0.5 rounded text-gray-600 text-xs"
                                    >
                                        Inactive
                                    </span>
                                </div>
                            </div>

                            <div class="flex items-center space-x-2">
                                <tooltip v-if="promoCodeAlreadySaved() && !promotionCodeExpired(promoCode?.expiration_date_value)"
                                         :content="promoCode?.active ? 'Archive promo code' : 'Unarchive promo code'"
                                >
                                    <div class="flex">
                                        <button type="button" class="focus:outline-none" @click.prevent="toggleActivePromoCode()">
                                            <icon :name="promoCode.active ? 'archive' : 'unarchive'" size="20" color="red"/>
                                        </button>
                                    </div>
                                </tooltip>
                            </div>

                        </div>

                        <div class="space-y-8 p-6">
                            <div class="bg-gray-50 p-6 rounded space-y-4">
                                <div>
                                    <div class="block mb-2.5 text-sm">
                                        Promotion code structure:
                                    </div>
                                    <div class="container mx-auto">
                                        <div>

                                            <text-field
                                                placeholder="Prefix"
                                                type="text"
                                                v-model="promoCode.auto_generate_promotion_code_name_custom_prefix"
                                                class="w-1/2 pr-2 pb-2"
                                            >
                                            </text-field>

                                            <div class="flex items-end">
                                                <checkbox-group
                                                v-model="promoCode.auto_generate_promotion_code_name_fields"
                                                :options="autoGeneratePromotionCodeNameBuilderAvailableFields"
                                                layout="horizontal"
                                            />
                                                <div v-if="promoCode.auto_generate_promotion_code_name_fields.includes('random_characters')">
                                                    <div>
                                                        <select
                                                            v-model="promoCode.auto_generate_promotion_code_name_random_characters_count"
                                                            class="block border-0 focus:ring-0 leading-5 rounded text-right text-xs text-gray-500 bg-transparent pl-6 pr-8 py-0"
                                                        >
                                                            <option :value="2">2 characters</option>
                                                            <option v-for="option in autoGeneratePromotionCodeNameFieldRandomSymbolsCountOptions" :value="option.value">
                                                                {{ option.label }}
                                                            </option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="mt-4">
                                                <label class="block text-gray-700 text-sm font-bold mb-2">Example:</label>
                                                <text-field
                                                    type="text"
                                                    :model-value="autoGeneratePromotionCodeNameFieldExample()"
                                                    placeholder="Generated code"
                                                    :disabled="true"
                                                >
                                                    <template #note>
                                                        If you do not select any field, the default promotion code name
                                                        will be generated.
                                                        For already taken names, the system will add an ordinal number
                                                        to the codename.
                                                    </template>
                                                </text-field>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div class="flex justify-between items-center">
                                    <toggle
                                        v-model="promoCode.first_time_order"
                                        :disabled="promoCodeAlreadySaved()"
                                    >
                                    </toggle>
                                    <span class="flex flex-col flex-grow" id="first_time_order-label">
                                            <span class="text-sm font-medium text-gray-900">
                                                Eligible for first-time order only
                                            </span>
                                            <span class="text-sm text-gray-500">
                                                If enabled, coupon will be valid only for first time order i.e. for customers who have never made a purchase.
                                            </span>
                                        </span>
                                </div>
                            </div>
                            <div class="grid grid-cols-2 gap-8">
                                <div>
                                    <label for="promo_code_min_status" class="block mb-1.5 text-sm">
                                        Minimum order value
                                    </label>
                                    <div
                                        class="grid gap-1"
                                        :class="promoCode.minimum_order_status === '0' ? 'grid-cols-1' : 'grid-cols-2'"
                                    >
                                        <select
                                            id="promo_code_min_status"
                                            v-model="promoCode.minimum_order_status"
                                            class="block w-full rounded border-gray-300 sm:text-sm focus:ring-0 focus:border-gray-400"
                                            :disabled="promoCodeAlreadySaved()"
                                        >
                                            <option value="0">OFF</option>
                                            <option value="1">ON</option>
                                        </select>
                                        <text-field
                                            v-if="promoCode.minimum_order_status !== '0'"
                                            v-model="promoCode.minimum_order_value"
                                            :disabled="promoCode.minimum_order_status === '0' || promoCodeAlreadySaved()"
                                            :required="promoCode.minimum_order_status !== '0'"
                                        >
                                        </text-field>
                                    </div>
                                    <small class="text-gray-500 mt-1 block">
                                        Set an minimum amount that is eligible to use a promotion code.
                                    </small>
                                </div>
                            </div>
                            <div class="grid grid-cols-2 gap-8">
                                <redemption-specific-date-period
                                    id="spec-date-period-promo-code"
                                    name="Expiration date"
                                    description="Limit the date when customers can redeem this promotion code."
                                    v-model:type="promoCode.expiration_date_status"
                                    v-model:redemption_value="promoCode.expiration_date_value"
                                    :disabled="promoCodeAlreadySaved()"
                                />
                                <redemption-times-period
                                    name="Number of times"
                                    description="Limit the total number of times this code can be redeemed."
                                    v-model:type="promoCode.redemption_times_status"
                                    v-model:redemption_value="promoCode.redemption_times_value"
                                    :disabled="promoCodeAlreadySaved()"
                                />
                            </div>
                            <hr />
                            <div>
                                <label class="block mb-1.5 text-sm">
                                    Metadata
                                </label>
                                <div
                                    v-for="(metadata, metadataIndex) in promoCode.metadata"
                                    :key="'promoCode-' + '-metadata-' + metadataIndex"
                                    class="grid grid-cols-5 gap-4 mb-2"
                                >
                                    <text-field
                                        class="md:col-span-2"
                                        type="text"
                                        v-model="metadata.key"
                                        placeholder="Key"
                                    >
                                    </text-field>
                                    <text-field
                                        class="col-span-3"
                                        type="text"
                                        v-model="metadata.value"
                                        placeholder="Value"
                                    >
                                        <template #append>
                                            <div class="inline-flex items-center pl-2 text-gray-500 cursor-pointer">
                                                <icon
                                                    name="trash"
                                                    size="18"
                                                    @click="removeMetadata(metadataIndex)"
                                                />
                                            </div>
                                        </template>
                                    </text-field>
                                </div>
                                <btn type="secondary"
                                     icon-name="add-circle"
                                     @click="addMetadata()"
                                     size="small"
                                >
                                    Add metadata
                                </btn>
                            </div>
                        </div>
                    </div>
                </div>

            </template>
            <template #footer>
                <btn v-if="promotionCodeAction === 'create'"
                     type="primary"
                     @click="saveAutoAssignablePromotionCode()"
                     :disabled="savingAutoAssignablePromotionCode"
                >
                    <spinner
                        v-if="savingAutoAssignablePromotionCode"
                        :is-white="true"
                        class="mr-1.5"
                    />
                    {{ savingAutoAssignablePromotionCode ? 'Creating...' : 'Create' }}
                </btn>
                <btn v-if="editingAction && promoCodeAlreadySaved()"
                     type="primary"
                     @click="updateAutoAssignablePromotionCodeMetadata()"
                     :disabled="updatingAutoAssignablePromotionCode"
                >
                    <spinner
                        v-if="updatingAutoAssignablePromotionCode"
                        :is-white="true"
                        class="mr-1.5"
                    />
                    {{ updatingAutoAssignablePromotionCode ? 'Updating...' : 'Update' }}
                </btn>
            </template>
        </modal>
    </div>
</template>

<style scoped>

</style>
